/**
 * Color-scheme theming. The CSS lives in `styles/tokens.css` — adding the
 * `theme-dark` class to <html> flips every design token to the dark ramp.
 *
 * The user's choice is persisted server-side on their profile (the source of
 * truth, so it follows them across devices) and mirrored into localStorage so
 * the inline bootstrap script in `index.html` can paint the right theme before
 * React mounts (no flash-of-light-theme on reload). The storage key here must
 * stay in sync with that inline script.
 */
import { useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'trulyinbox-theme';
const DARK_CLASS = 'theme-dark';

/** Apply a theme to <html> and remember it for the next cold load. */
export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle(DARK_CLASS, theme === 'dark');
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // localStorage may be unavailable (private mode / SSR) — the in-memory
    // class toggle above is enough for the current session.
  }
}

/**
 * The theme currently painted on `<html>`, kept live.
 *
 * Reads the class `applyTheme` sets, not the profile query — so a component
 * that only needs to colour itself takes no network dependency, and reads what
 * the tokens around it actually resolved to. The `MutationObserver` is what
 * keeps it reactive; React cannot see a class toggle on its own.
 */
export function useAppliedTheme(): Theme {
  return useSyncExternalStore(subscribeToTheme, getAppliedTheme, () => 'light');
}

function getAppliedTheme(): Theme {
  return document.documentElement.classList.contains(DARK_CLASS) ? 'dark' : 'light';
}

function subscribeToTheme(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
  return () => observer.disconnect();
}
